import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import PatientIntakeTab from './PatientIntakeTab';
import PatientAttachmentsView from '../../components/PatientAttachmentsView';
import { UserSelectors } from '../../reducers/user';

const Page = styled.div`
  padding: 24px;
  width: 100%;
  max-width: min(1920px, 100%);
  margin: 0 auto;
  box-sizing: border-box;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  color: #111827;
  margin: 0 0 20px 0;
`;

const TabBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 20px;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 2px;
`;

const Tab = styled.button<{ $active: boolean }>`
  padding: 10px 14px;
  font-size: 0.8125rem;
  font-weight: 600;
  border: 1px solid ${p => (p.$active ? '#3b82f6' : 'transparent')};
  border-bottom: none;
  border-radius: 10px 10px 0 0;
  background: ${p => (p.$active ? '#eff6ff' : 'transparent')};
  color: ${p => (p.$active ? '#1d4ed8' : '#64748b')};
  cursor: pointer;
  margin-bottom: -2px;
  &:hover {
    color: #1e40af;
    background: ${p => (p.$active ? '#eff6ff' : '#f8fafc')};
  }
`;

const Placeholder = styled.div`
  padding: 48px 24px;
  text-align: center;
  color: #64748b;
  font-size: 0.9375rem;
  background: #f9fafb;
  border-radius: 12px;
  border: 1px dashed #e5e7eb;
`;

const TABS = [
  { id: 'intake',   label: 'Patient Intake' },
  { id: 'chyps',   label: 'CHYPS Assessment' },
  { id: 'cosidi',  label: 'COSIDI' },
  { id: 'symptom', label: 'Symptom Timeline' },
] as const;

type TabId = (typeof TABS)[number]['id'];

const SymptomsProfilePage: React.FC = () => {
  const isPatientSession = useSelector(UserSelectors().isPatientSession);
  const [tab, setTab] = useState<TabId>('intake');

  if (isPatientSession) {
    return (
      <Page>
        <PageTitle>Symptom Profile</PageTitle>
        <PatientAttachmentsView title="My Intake Documents" />
      </Page>
    );
  }

  return (
    <Page>
      <PageTitle>Symptom Profile</PageTitle>
      <TabBar>
        {TABS.map(t => (
          <Tab key={t.id} $active={tab === t.id} onClick={() => setTab(t.id)}>
            {t.label}
          </Tab>
        ))}
      </TabBar>

      {tab === 'intake' && <PatientIntakeTab />}
      {tab === 'chyps' && (
        <Placeholder>
          CHYPS Assessment — symptom questionnaire links and responses coming soon.
        </Placeholder>
      )}
      {tab === 'cosidi' && (
        <Placeholder>
          COSIDI — Client Oriented Scale of Improvement for Dizziness and Imbalance coming soon.
        </Placeholder>
      )}
      {tab === 'symptom' && (
        <Placeholder>
          Symptom Timeline — longitudinal symptom tracking coming soon.
        </Placeholder>
      )}
    </Page>
  );
};

export default SymptomsProfilePage;
